import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  TrendingUp,
  DollarSign,
  Target,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import api from '../api';
import { Deal, Activity } from '../types';

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
};

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR');
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [totalCompanies, setTotalCompanies] = useState<number>(0);
  const [activeDeals, setActiveDeals] = useState<number>(0);
  const [pipelineValue, setPipelineValue] = useState<number>(0);
  const [winRate, setWinRate] = useState<number>(0);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [overdueDeals, setOverdueDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError('');
      try {
        const [companiesRes, dealsRes, pipelineRes, activitiesRes] = await Promise.all([
          api.get('/companies', { params: { limit: 1, page: 1 } }),
          api.get('/deals', { params: { limit: 1, page: 1 } }),
          api.get('/deals/pipeline-stats').catch(() => ({ data: null })),
          api.get('/activities', { params: { limit: 10, page: 1 } }),
        ]);

        setTotalCompanies(companiesRes.data.total || 0);
        setActiveDeals(dealsRes.data.total || 0);

        if (pipelineRes.data) {
          const stats = pipelineRes.data;
          setPipelineValue(stats.totalValue || 0);
          const total = (stats.wonCount || 0) + (stats.lostCount || 0);
          setWinRate(total > 0 ? Math.round(((stats.wonCount || 0) / total) * 100) : 0);
        }

        setRecentActivities(activitiesRes.data.data || activitiesRes.data || []);

        // Fetch deals to find overdue ones
        const allDealsRes = await api.get('/deals', { params: { limit: 100, page: 1 } });
        const deals: Deal[] = allDealsRes.data.data || allDealsRes.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const overdue = deals.filter((d) => {
          if (!d.nextStepDueDate || d.outcome) return false;
          const dueDate = new Date(d.nextStepDueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate < today;
        });
        setOverdueDeals(overdue);
      } catch (err) {
        setError('Erreur lors du chargement du tableau de bord.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="spinner-container">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertTriangle size={48} />
        <p className="empty-state-title">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Tableau de bord</h1>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-card-header">
            <div>
              <div className="kpi-value">{totalCompanies.toLocaleString('fr-FR')}</div>
              <div className="kpi-label">Entreprises</div>
            </div>
            <div className="kpi-card-icon blue">
              <Building2 size={20} />
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-card-header">
            <div>
              <div className="kpi-value">{activeDeals.toLocaleString('fr-FR')}</div>
              <div className="kpi-label">Affaires actives</div>
            </div>
            <div className="kpi-card-icon green">
              <TrendingUp size={20} />
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-card-header">
            <div>
              <div className="kpi-value">{formatCurrency(pipelineValue)}</div>
              <div className="kpi-label">Valeur pipeline</div>
            </div>
            <div className="kpi-card-icon purple">
              <DollarSign size={20} />
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-card-header">
            <div>
              <div className="kpi-value">{winRate}%</div>
              <div className="kpi-label">Taux de conversion</div>
            </div>
            <div className="kpi-card-icon orange">
              <Target size={20} />
            </div>
          </div>
        </div>
      </div>

      <div className="two-col">
        {/* Recent Activities */}
        <div className="card">
          <div className="card-header">
            <h3>Activités récentes</h3>
          </div>
          <div className="card-body">
            {recentActivities.length === 0 ? (
              <p className="text-secondary text-center" style={{ padding: '20px' }}>
                Aucune activité récente
              </p>
            ) : (
              <div className="timeline">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="timeline-item">
                    <div className="timeline-item-header">
                      <span className="timeline-item-title">{activity.subject}</span>
                      <span className={`badge badge-${getActivityBadgeColor(activity.type)}`}>
                        {getActivityTypeLabel(activity.type)}
                      </span>
                    </div>
                    <div className="timeline-item-date">
                      {formatDate(activity.activityDate)}
                      {activity.company && ` - ${activity.company.name}`}
                    </div>
                    {activity.description && (
                      <div className="timeline-item-body">{activity.description.substring(0, 100)}{activity.description.length > 100 ? '...' : ''}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Overdue Deals */}
        <div className="card">
          <div className="card-header">
            <h3>
              <span className="inline-flex">
                <Clock size={18} />
                Affaires en retard
                {overdueDeals.length > 0 && (
                  <span className="badge badge-danger">{overdueDeals.length}</span>
                )}
              </span>
            </h3>
          </div>
          <div className="card-body">
            {overdueDeals.length === 0 ? (
              <p className="text-secondary text-center" style={{ padding: '20px' }}>
                Aucune affaire en retard
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {overdueDeals.slice(0, 10).map((deal) => (
                  <div
                    key={deal.id}
                    className="card"
                    style={{ cursor: 'pointer', padding: '12px' }}
                    onClick={() => navigate(`/deals/${deal.id}`)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div className="font-semibold">{deal.title}</div>
                        <div className="text-secondary" style={{ fontSize: '0.85rem' }}>
                          {deal.company?.name}
                        </div>
                        <div className="text-secondary" style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                          {deal.nextStepDescription}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {deal.estimatedAmount !== undefined && deal.estimatedAmount !== null && (
                          <div className="font-bold text-primary">{formatCurrency(deal.estimatedAmount)}</div>
                        )}
                        <div className="text-danger" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          Échéance : {formatDate(deal.nextStepDueDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function getActivityTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    email: 'Email',
    call: 'Appel',
    meeting: 'Réunion',
    letter: 'Courrier',
    note: 'Note',
  };
  return labels[type] || type;
}

function getActivityBadgeColor(type: string): string {
  const colors: Record<string, string> = {
    email: 'info',
    call: 'success',
    meeting: 'purple',
    letter: 'warning',
    note: 'gray',
  };
  return colors[type] || 'gray';
}

export default DashboardPage;
